import React from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';

function splitSections(sections) {
  const midpoint = Math.ceil(sections.length / 2);
  return {
    firstHalf: sections.slice(0, midpoint),
    secondHalf: sections.slice(midpoint),
  };
}

function SectionHeading({ eyebrow, title, subtitle, centered = true, light = false }) {
  return (
    <div
      className={`landing-section-heading ${centered ? 'text-center' : ''} ${
        light ? 'landing-section-heading--light' : ''
      }`}
    >
      {eyebrow ? <span className="landing-section-heading__eyebrow">{eyebrow}</span> : null}
      {title ? <h2 className="landing-section-heading__title">{title}</h2> : null}
      {subtitle ? <p className="landing-section-heading__subtitle">{subtitle}</p> : null}
    </div>
  );
}

function SectionCard({ section, index, light = false }) {
  const items = Array.isArray(section?.items) ? section.items : [];

  return (
    <Col xl={4} lg={6} md={6} className="d-flex">
      <Card
        className={`saas-info-card w-100 border-0 ${
          light ? 'saas-info-card--on-dark' : ''
        }`}
      >
        <Card.Body className="saas-info-card__body">
          <div className={`saas-info-card__icon ${light ? 'saas-info-card__icon--light' : ''}`}>
            <span>{String(index + 1).padStart(2, '0')}</span>
          </div>

          {section?.title ? (
            <h3 className={`saas-info-card__title ${light ? 'saas-info-card__title--light' : ''}`}>
              {section.title}
            </h3>
          ) : null}

          {section?.description ? (
            <p
              className={`saas-info-card__description ${
                light ? 'saas-info-card__description--light' : ''
              }`}
            >
              {section.description}
            </p>
          ) : null}

          {items.length ? (
            <ul className={`saas-info-card__list ${light ? 'saas-info-card__list--light' : ''}`}>
              {items.map((item, itemIndex) => (
                <li key={`${section?.title || 'section'}-${itemIndex}`}>{item}</li>
              ))}
            </ul>
          ) : null}
        </Card.Body>
      </Card>
    </Col>
  );
}

export default function LandingOverviewGrid({ content, settings }) {
  const sections = Array.isArray(content?.overview_sections) ? content.overview_sections : [];

  if (!sections.length) return null;

  const { firstHalf, secondHalf } = splitSections(sections);

  const primary = settings?.primary_color || '#0d6efd';
  const secondary = settings?.secondary_color || '#4dabf7';

  const brandedSectionStyle = {
    '--overview-primary': primary,
    '--overview-secondary': secondary,
  };

  return (
    <>
      {firstHalf.length ? (
        <section className="landing-section saas-info-section saas-info-section--primary">
          <Container>
            <SectionHeading
              eyebrow={content?.overview_section_one_eyebrow || 'Platform Overview'}
              title={content?.overview_section_one_title || 'Everything you need to launch and scale'}
              subtitle={
                content?.overview_section_one_subtitle ||
                'Explore the core product capabilities that help teams automate faster, serve better, and grow with confidence.'
              }
            />

            <Row className="g-4">
              {firstHalf.map((section, index) => (
                <SectionCard
                  key={`${section?.title || 'overview-a'}-${index}`}
                  section={section}
                  index={index}
                />
              ))}
            </Row>
          </Container>
        </section>
      ) : null}

      {secondHalf.length ? (
        <section
          className="landing-section saas-info-section saas-info-section--branded"
          style={brandedSectionStyle}
        >
          <Container>
            <SectionHeading
              eyebrow={content?.overview_section_two_eyebrow || 'Why teams choose us'}
              title={content?.overview_section_two_title || 'Built for modern SaaS execution'}
              subtitle={
                content?.overview_section_two_subtitle ||
                'From deployment clarity to operational efficiency, these features give your business a stronger competitive edge.'
              }
              light
            />

            <Row className="g-4">
              {secondHalf.map((section, index) => (
                <SectionCard
                  key={`${section?.title || 'overview-b'}-${index}`}
                  section={section}
                  index={index + firstHalf.length}
                  light
                />
              ))}
            </Row>
          </Container>
        </section>
      ) : null}
    </>
  );
}